import React, { useState } from 'react';
import { TrendingUp, Lightbulb, Users, DollarSign, BarChart3, Target, Brain, Sparkles } from 'lucide-react';
import { TeacherMarketAnalysis } from '../../components/AI/Teacher/TeacherMarketAnalysis';
import { TeacherCourseRecommendations } from '../../components/AI/Teacher/TeacherCourseRecommendations';
import { TeacherCompetitorAnalysis } from '../../components/AI/Teacher/TeacherCompetitorAnalysis';
import { TeacherPricingSuggestions } from '../../components/AI/Teacher/TeacherPricingSuggestions';
import { TeacherPerformanceAnalysis } from '../../components/AI/Teacher/TeacherPerformanceAnalysis';
import { TeacherMarketInsights } from '../../components/AI/Teacher/TeacherMarketInsights';

type ActiveTab = 'market' | 'recommendations' | 'competitor' | 'pricing' | 'performance' | 'insights';

export const TeacherAIPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('market');

  const tabs = [
    {
      id: 'market' as const,
      label: '市场分析',
      icon: TrendingUp,
      description: 'AI分析教育市场趋势，发现商业机会'
    },
    {
      id: 'recommendations' as const,
      label: '课程推荐',
      icon: Lightbulb,
      description: '基于市场数据推荐最具潜力的课程方向'
    },
    {
      id: 'competitor' as const,
      label: '竞争分析',
      icon: Users,
      description: '分析竞争对手情况，制定差异化策略'
    },
    {
      id: 'pricing' as const,
      label: '定价建议',
      icon: DollarSign,
      description: '智能定价策略，提升收入和竞争力'
    },
    {
      id: 'performance' as const,
      label: '表现分析',
      icon: BarChart3,
      description: '全面分析教学表现，发现提升空间'
    },
    {
      id: 'insights' as const,
      label: '市场洞察',
      icon: Target,
      description: '深度市场洞察，把握教育行业脉搏'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'market':
        return <TeacherMarketAnalysis />;
      case 'recommendations':
        return <TeacherCourseRecommendations />;
      case 'competitor':
        return <TeacherCompetitorAnalysis />;
      case 'pricing':
        return <TeacherPricingSuggestions />;
      case 'performance':
        return <TeacherPerformanceAnalysis />;
      case 'insights':
        return <TeacherMarketInsights />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-3 mb-4">
              <div className="p-3 bg-white/20 rounded-full">
                <Brain className="h-8 w-8" />
              </div>
              <h1 className="text-4xl font-bold">教师AI智能助手</h1>
            </div>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
              专为教师打造的AI商业智能平台，提供市场分析、课程推荐、竞争洞察和定价策略
            </p>
          </div>
          
          {/* 功能特色 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                icon: TrendingUp,
                title: '市场分析',
                description: '实时分析教育市场动态，识别最具潜力的教学领域'
              },
              {
                icon: Lightbulb,
                title: '智能推荐',
                description: '基于数据科学推荐最适合的课程内容和教学策略'
              },
              {
                icon: DollarSign,
                title: '商业洞察',
                description: '竞争分析、定价策略、收入优化等全方位商业支持'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
                <feature.icon className="h-12 w-12 mx-auto mb-4 text-emerald-200" />
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-emerald-100 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 标签导航 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 标签说明 */}
      <div className="bg-emerald-50 border-b border-emerald-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-emerald-700 text-sm">
            {tabs.find(tab => tab.id === activeTab)?.description}
          </p>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>
    </div>
  );
}; 